import type { MedusaProduct, ProductsCollection } from "../../types";
import CFG from "../../store.config";
import type { ProductFetchEvent } from "../../types/dom";
import {
  COLLECTION_2_CARDS,
  COLLECTION_3_CARDS,
  COLLECTION_ACCESSORIES,
  COLLECTION_NWE_RINGS,
  COLLECTION_RINGS,
} from "../../data/tangem-wallets.ts";
import { getGeo } from "@lib/tangem.ts";

// For sorting by size
const parseSizeFromRingSku = (sku: string) =>
  sku.match(/\d+/) ? parseInt(sku.match(/\d+/)![0], 10) : 0;
import type {
  CartCreateResponse,
  CompleteResponse,
  Region,
  ShippingOption,
} from "@lib/store/types";
import type { CartResponse } from "@lib/store/types/entities/cart.type.ts";
import { fetchWithRetries } from "@lib/utils/fetch-retry.ts";
import { FREE_SHIPPING_DEFAULT_AMOUNT } from "@components/WalletForm/models/freeShipping.ts";
import type { DicountNotFound, DiscountCode } from "./types/discount.ts";

type LineItem = {
  variant_id: string;
  quantity: number;
};

type ProductsDto = {
  products: MedusaProduct[];
  freeShippingThresholdCost?: number;
};

const getIsProductRestricted = (
  product: MedusaProduct,
  geoCode: string | undefined,
) => {
  if (!product.metadata?.["restricted-sales-countries"]) return false;
  // assuming we're in the restricted country
  if (!geoCode && product.metadata?.["restricted-sales-countries"]) return true;
  return product.metadata?.["restricted-sales-countries"]?.split(",").map(code => code.trim()).includes(geoCode);
};

class API {
  private readonly _tangem_store_uri = CFG.TANGEM_BACKEND_URL; // 'https://backendstore.tangem.org'; // 'http://localhost:9000';

  private async post<T>(
    url: string,
    bodyData: unknown = null,
    headers: HeadersInit = {},
  ) {
    const options: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    };

    if (bodyData) {
      options["body"] = JSON.stringify(bodyData);
    }

    return fetchWithRetries<T>(url, options);
  }

  private async get<T = Record<string, unknown>>(
    url: string,
    headers: HeadersInit = {},
  ): Promise<T> {
    return fetchWithRetries<T>(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    });
  }

  private async delete<T>(url: string, headers: HeadersInit = {}) {
    return fetchWithRetries<T>(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    });
  }

  createCart(params: unknown) {
    return this.post<CartCreateResponse>(
      `${this._tangem_store_uri}/store/carts`,
      params,
    );
  }

  updateCart<T>(cartId: string, newData: T) {
    return this.storePost(`carts/${cartId}`, newData);
  }

  async loadCartData(cartId: string) {
    const result = await this.get(
      `${this._tangem_store_uri}/store/carts/${cartId}`,
    );
    return result?.cart;
  }

  async removeLineItem(cartId: string, lineItemId: string) {
    const result = await this.delete<CartResponse>(
      `${this._tangem_store_uri}/store/carts/${cartId}/line-items/${lineItemId}`,
    );
    return result.cart;
  }

  addLineItem(cartId: string, lineItemData: LineItem) {
    return this.post(
      `${this._tangem_store_uri}/store/carts/${cartId}/line-items`,
      lineItemData,
    );
  }

  async loadProducts(
    collections: Array<ProductsCollection> = [
      COLLECTION_2_CARDS,
      COLLECTION_3_CARDS,
      COLLECTION_RINGS,
      COLLECTION_ACCESSORIES,
    ],
  ) {
    const geo = await getGeo().catch(() => undefined);
    return this.get<ProductsDto>(
      `${
        this._tangem_store_uri
      }/store/products-extended?collections=${collections?.join(",")}`,
    )
      .then((data) => {
        const productsData = data?.products
          ?.filter((product) => {
            const geoCode = geo?.code;
            const { status, images, variants, collection } = product;

            const isPublished = status === "published";
            const hasImages = images.length > 1;
            const hasPreviewImage = images.some((image) =>
              image.url.includes("preview"),
            );
            const hasVariants = variants.length > 0;
            const isInRingCollection =
              (collections.includes(collection.title) &&
                collection.title === COLLECTION_RINGS) ||
              collection.title === COLLECTION_NWE_RINGS;
            const isInAccessoriesCollection =
              collections.includes(collection.title) &&
              collection.title === COLLECTION_ACCESSORIES;
            const isRestrictedByCountry = getIsProductRestricted(product, geoCode);

            return (
              isPublished && !isRestrictedByCountry &&
              ((hasImages && hasPreviewImage) ||
                (isInRingCollection && hasVariants) ||
                (isInAccessoriesCollection && hasVariants))
            );
          })
          .sort(
            (a, b) =>
              parseInt(a.metadata?.["sort-index"] ?? "0") -
              parseInt(b.metadata?.["sort-index"] ?? "0"),
          );
        const freeShippingThresholdCost =
          data?.freeShippingThresholdCost ?? FREE_SHIPPING_DEFAULT_AMOUNT;
        return { products: productsData, freeShippingThresholdCost };
      })
      .catch((e) => {
        const productRequestError = new CustomEvent<ProductFetchEvent>(
          "products_request_fail",
          {
            detail: {
              cause: e.options?.cause || null,
              message: e.message,
            },
          },
        );
        if (typeof document !== "undefined") {
          document.dispatchEvent(productRequestError);
        }
        // eslint-disable-next-line no-console
        return {
          products: new Array<MedusaProduct>(),
          freeShippingThresholdCost: FREE_SHIPPING_DEFAULT_AMOUNT,
        };
      });
  }

  async loadRingProducts(
    collections: Array<ProductsCollection> = [COLLECTION_RINGS],
  ) {
    return this.get<ProductsDto>(
      `${
        this._tangem_store_uri
      }/store/products-extended?collections=${collections?.join(",")}`,
    )
      .then((data) =>
        data?.products
          ?.filter(
            (product) =>
              product.status === "published" && product.variants.length > 0,
          )
          .sort(
            (a, b) =>
              parseSizeFromRingSku(a.variants[0].sku) -
              parseSizeFromRingSku(b.variants[0].sku),
          ),
      )
      .catch((e) => {
        const productRequestError = new CustomEvent<ProductFetchEvent>(
          "products_request_fail",
          {
            detail: { cause: e.options?.cause || null, message: e.message },
          },
        );
        if (typeof document !== "undefined") {
          document.dispatchEvent(productRequestError);
        }
        // eslint-disable-next-line no-console
        console.log("loadRingProducts CATCH: ", e);
        return new Array<MedusaProduct>();
      });
  }

  async getDiscountByCode(code: string | null) {
    const lowerCaseCode = (code ?? "").toLowerCase();
    return await this.get<DiscountCode | DicountNotFound>(
      `${this._tangem_store_uri}/discount/${lowerCaseCode}`,
    ).catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
      throw new Error(e);
    });
  }

  async applyMultipleDiscounts(cartId: string, discountCodes: string[]) {
    return await this.post(
      `${this._tangem_store_uri}/discount/apply-multiple`,
      {
        cartId,
        discountCodes,
      },
    ).catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
      throw new Error(e);
    });
  }

  initPaymentSessions(cartId: string) {
    return this.storePost(`carts/${cartId}/payment-sessions`);
  }

  selectPaymentSession(cartId: string, providerId: string) {
    return this.post(
      `${this._tangem_store_uri}/store/carts/${cartId}/payment-session`,
      {
        provider_id: providerId,
      },
    );
  }

  regions() {
    return this.storeGet<{ regions: Region[] }>("regions");
  }

  shippingOptions() {
    return this.storeGet<{ shipping_options: ShippingOption[] }>(
      "shipping-options",
    );
  }

  applyShippingOptions(cartId: string, optionId: string) {
    return this.storePost<CartResponse>(`carts/${cartId}/shipping-methods`, {
      option_id: optionId,
    });
  }

  checkFreeShipping(cartId: string) {
    return this.storeGet<CartResponse["cart"]>(
      `carts-extended/${cartId}/free-shipping`,
    );
  }

  private storeGet<T>(method: string, headers: HeadersInit = {}) {
    return this.get<T>(`${this._tangem_store_uri}/store/${method}`, headers);
  }

  private async storePost<T>(
    method: string,
    bodyData = {} as unknown,
    headers: HeadersInit = {},
  ): Promise<T> {
    return this.post<T>(
      `${this._tangem_store_uri}/store/${method}`,
      bodyData,
      headers,
    );
  }

  private async storeDelete(method: string, headers: HeadersInit = {}) {
    return this.delete(`${this._tangem_store_uri}/store/${method}`, headers);
  }

  refreshPaymentSessions(id: string, providerId: string) {
    return this.storePost(`carts/${id}/payment-sessions/${providerId}`, {
      data: { data: null },
    });
  }

  updatePaymentSessions(id: string, providerId: string, data: unknown) {
    return this.storePost<CartResponse>(
      `carts/${id}/payment-sessions/${providerId}`,
      data,
    );
  }

  completeCart(id: string) {
    return this.storePost<CompleteResponse>(`carts/${id}/complete`);
  }

  deletePaymentSession(cartId: string, providerId: string) {
    return this.storeDelete(`carts/${cartId}/payment-sessions/${providerId}`);
  }
}

export default new API();
